import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import ShopHourForm from './ShopHourForm';
import { utcInputFromNow, utcInputToIso } from '../../utils/utcDateTime';
import 'react-quill/dist/quill.snow.css';

const createInitialFormState = () => {
  const now = utcInputFromNow();

  return {
    name: '',
    description: '',
    startTime: now,
    endTime: now,
    backgroundImageUrl: '',
    backgroundMobileImageUrl: '',
    backgroundImageBrightness: '',
    backgroundMobileImageBrightness: '',
    backgroundThemeOverride: '',
    backgroundMobileThemeOverride: '',
    shopLogoImageUrl: '',
    shopButtonImageUrl: '',
    shopDescriptionImageUrl: '',
    shopVideoUrl: '',
    shopDescription: '',
    active: false,
    shopHoursPassed: false,
  };
};

const CreateShopHourModal = ({ show, onHide, onCreate, token }) => {
  const [formData, setFormData] = useState(() => createInitialFormState());
  const [dateError, setDateError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);

  useEffect(() => {
    if (show) {
      setFormData(createInitialFormState());
      setDateError('');
      setIsSaving(false);
    }
  }, [show]);

  const validateDates = () => {
    if (formData.endTime <= formData.startTime) {
      setDateError('End time must be after start time.');
      return false;
    }
    setDateError('');
    return true;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (field === 'startTime' || field === 'endTime') {
      validateDates();
    }
  };

  const handleSubmit = async () => {
    if (!validateDates()) return;

    setIsSaving(true);
    try {
      const normalizeBrightness = value => {
        if (value === '' || value === null || value === undefined) {
          return null;
        }
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
      };

      const normalizeTheme = value => (value ? value : null);

      const shopHourData = {
        ...formData,
        startTime: utcInputToIso(formData.startTime),
        endTime: utcInputToIso(formData.endTime),
        backgroundImageBrightness: normalizeBrightness(formData.backgroundImageBrightness),
        backgroundMobileImageBrightness: normalizeBrightness(
          formData.backgroundMobileImageBrightness
        ),
        backgroundThemeOverride: normalizeTheme(formData.backgroundThemeOverride),
        backgroundMobileThemeOverride: normalizeTheme(formData.backgroundMobileThemeOverride),
      };
      await onCreate({ shopHours: [shopHourData] });
      onHide();
    } catch (error) {
      console.error('Error creating shop hour:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        if (!videoBusy) onHide();
      }}
      centered
      size="xl"
      dialogClassName="shop-hour-modal"
    >
      <Modal.Header closeButton={!videoBusy}>
        <Modal.Title>Create Shop Hour</Modal.Title>
      </Modal.Header>
      <Modal.Body className="py-4">
        <ShopHourForm
          formData={formData}
          onChange={handleChange}
          dateError={dateError}
          onVideoBusyChange={setVideoBusy}
          showShopHoursPassedToggle={false}
          token={token}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={videoBusy}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSaving || videoBusy}
          title={videoBusy ? 'Wait for the video upload to finish' : undefined}
        >
          {isSaving ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
            </>
          ) : (
            'Save'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateShopHourModal;
